/**
 * Entity Drilldown page (/projects/$projectId/items/$itemId).
 *
 * The single most important visual in the product (UX §8): a vertical 18-stage
 * pipeline view for one PART (or SUB_PART), grouped into the five clusters
 * — Procurement, Preparation, Inspection, Delivery, Billing.
 *
 * Layout:
 *
 *   [breadcrumb back to project] [item header: name, sr#, type, qty]
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ PROCUREMENT     1.  Order placed                          │
 *   │                 2.  Pre-shipment inspection               │
 *   │                 3.  In transit                            │
 *   │                 4.  Arrived                               │
 *   │                 5.  In-house inspection  · 2 of 3         │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ PREPARATION     6.  Assembling           · [date] [Record]│
 *   │                 7.  Markings / casing                     │
 *   │                 8.  Inspection offered                     │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ INSPECTION      9.  Inspection  [ACCEPTED]  · 2026-03-25  │
 *   │                 10. Action plan                           │
 *   │                 11. Re-inspection                         │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ DELIVERY        12. Delivered                             │
 *   │                 13. Functional check                      │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ BILLING         14. I-Note received                       │
 *   │                 15. CRV issued                            │
 *   │                 16. Invoice submitted [BG-locked]         │
 *   │                 17. Cheque released                       │
 *   │                 18. Cheque cleared                        │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Editing model:
 *   - Stages 1-5 are read-only here (they're edited via the Batches tab).
 *   - Stages 6, 7, 8, 10, 12, 14, 15, 16, 17, 18 have an inline date input
 *     when in the 'current' state.
 *   - Stages 9, 11, 13 open the InspectionEventDrawer (outcome + resolved
 *     date + location + notes).
 *   - Locked stages show the inline reason so the user knows what unlocks.
 */

import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { formatBpsAsPercent, formatCurrency } from '@procurement/shared';
import { BatchSurface } from '@/components/batch-surface';
import { ErrorPanel } from '@/components/ui/error-panel';
import { InspectionEventDrawer } from '@/components/inspection-event-drawer';
import { PipelineStageRow } from '@/components/pipeline-stage-row';
import {
  computePipelineState,
  groupByCluster,
  type StageRowState,
} from '@/lib/pipeline-state';
import {
  useBankGuarantees,
  useInspectionEvents,
  usePartStageDates,
  useProjectItems,
  useUpsertPartStageDate,
  type InspectionStageNumber,
} from '@/lib/pipeline-queries';
import { useProject } from '@/lib/project-queries';
import { useAttachmentCounts } from '@/lib/attachments-queries';
import { usePageTitle } from '@/lib/use-page-title';
import { useCurrentUser } from '@/lib/auth';

export function EntityDrilldownPage() {
  const { projectId, itemId } = useParams({ strict: false }) as {
    projectId: string;
    itemId: string;
  };

  const projectQ = useProject(projectId);
  const itemsQ = useProjectItems(projectId);
  const stagesQ = usePartStageDates(projectId, itemId);
  const eventsQ = useInspectionEvents(projectId, itemId);
  const bgsQ = useBankGuarantees(projectId);
  // Patch 4 — attachment counts power the inspection-event badge inside the
  // drawer. Cheap to fetch (counts response is small); cached for 60s.
  const countsQ = useAttachmentCounts(projectId);
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';

  const upsertStage = useUpsertPartStageDate(projectId, itemId);

  const [inspectionStage, setInspectionStage] = useState<InspectionStageNumber | null>(null);

  // Dynamic title: "{itemName} — {projectNumber}" once both load.
  const focusItem = itemsQ.data?.find((it) => it.id === itemId);
  usePageTitle(
    focusItem && projectQ.data
      ? `${focusItem.name} — ${projectQ.data.projectNumber}`
      : null,
  );

  if (projectQ.isLoading || itemsQ.isLoading) {
    return <div className="px-8 py-10 text-sm text-secondary">Loading…</div>;
  }
  // Patch 5 — distinguish "project really missing" (404) from "API errored".
  // The existing "Project not found" branch below correctly handles
  // !projectQ.data, but only when the request resolved successfully.
  if (projectQ.isError || itemsQ.isError) {
    return (
      <div className="px-8 py-10">
        <ErrorPanel
          title="Could not load this entity"
          onRetry={() => {
            void projectQ.refetch();
            void itemsQ.refetch();
          }}
        />
      </div>
    );
  }

  const project = projectQ.data;
  if (!project) {
    return <div className="px-8 py-10 text-sm text-danger-strong">Project not found.</div>;
  }

  const item = itemsQ.data?.find((i) => i.id === itemId);
  if (!item) {
    return (
      <div className="px-8 py-10 text-sm text-danger-strong">
        Item not found in this project.
        <Link
          to="/projects/$projectId"
          params={{ projectId }}
          className="ml-2 text-accent-strong hover:underline"
        >
          Back to project
        </Link>
      </div>
    );
  }

  const hasAcceptedBg = (bgsQ.data ?? []).some((b) => b.status === 'ACCEPTED');

  const rows = computePipelineState({
    item,
    partStageDates: stagesQ.data ?? [],
    inspectionEvents: eventsQ.data ?? [],
    hasAcceptedBg,
    isProjectClosed: project.projectClosedAt !== null,
    userIsAdmin: isAdmin,
  });
  const groups = groupByCluster(rows);

  const latestInspectionEventForStage =
    inspectionStage !== null
      ? (eventsQ.data ?? [])
          .filter((e) => e.stageNumber === inspectionStage)
          .sort((a, b) => b.eventRound - a.eventRound)[0] ?? null
      : null;

  const isReFunctionalCheckCreate =
    inspectionStage === 13 &&
    latestInspectionEventForStage?.eventRound === 1 &&
    latestInspectionEventForStage.outcome !== 'ACCEPTED' &&
    latestInspectionEventForStage.resolvedDate !== null;

  const inspectionEventForStage = isReFunctionalCheckCreate
    ? null
    : latestInspectionEventForStage;

  async function handleSubmitDate(stageNumber: number, completedDate: string) {
    if (stageNumber === 6 || stageNumber === 7 || stageNumber === 8 || stageNumber === 10 ||
        stageNumber === 12 || stageNumber === 14 || stageNumber === 15 || stageNumber === 16 ||
        stageNumber === 17 || stageNumber === 18) {
      await upsertStage.mutateAsync({ stageNumber, completedDate });
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Link
          to="/projects/$projectId"
          params={{ projectId }}
          className="inline-flex items-center gap-1 text-tertiary hover:text-secondary"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          {project.projectTitle}
        </Link>
      </div>

      {/* Item header */}
      <div className="mb-6 border-b border-border-default pb-4">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-primary">{item.name}</h1>
          <span className="inline-flex items-center rounded-sm border border-border-default bg-subtle px-1.5 py-0.5 text-xs font-medium text-secondary">
            {item.itemType === 'PART' ? 'Part' : 'Sub-part'}
          </span>
          {item.srNumber !== null ? (
            <span className="text-xs text-secondary">Sr# {item.srNumber}</span>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-secondary">
          <span>{item.itemType === 'PART' ? 'Part #' : 'Sub-part #'} {item.itemNumber ?? '—'}</span>
          {item.itemType === 'PART' ? <span>UOM {item.uom ?? '—'}</span> : null}
          {item.itemType === 'PART' ? (
            <>
              <span>Unit price {item.clientUnitPricePaisa != null ? formatCurrency(item.clientUnitPricePaisa) : '—'}</span>
              <span>Value {item.valuePaisa != null ? formatCurrency(item.valuePaisa) : '—'}</span>
              <span>GST {formatBpsAsPercent(item.gstRateBps)}</span>
              <span>Total {item.totalValuePaisa != null ? formatCurrency(item.totalValuePaisa) : '—'}</span>
            </>
          ) : null}
          {item.totalRequiredQuantity > 0 ? (
            <span>
              {item.activeOrderedQuantity} of {item.totalRequiredQuantity} ordered
            </span>
          ) : null}
          {item.partialBatchIndicator ? <span>· {item.partialBatchIndicator}</span> : null}
          {project.projectClosedAt ? (
            <span className="inline-flex items-center gap-1 text-warning-strong">
              <AlertCircle className="h-3 w-3" strokeWidth={1.5} />
              Project closed — read only
            </span>
          ) : null}
        </div>
      </div>

      {/* Order batches surface (procurement stages 1-5) */}
      <div className="mb-4">
        <BatchSurface
          project={project}
          item={item}
          subParts={
            item.itemType === 'PART' && item.hasSubParts
              ? (itemsQ.data ?? []).filter((i) => i.parentItemId === item.id)
              : undefined
          }
        />
      </div>

      {/* Stage clusters */}
      <div className="rounded-md border border-border-default bg-surface">
        {groups.map((group) => (
          <div key={group.cluster} className="border-b border-border-default last:border-b-0">
            <div className="flex items-baseline justify-between border-b border-border-subtle px-4 py-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-tertiary">
                {group.label}
              </h2>
              <ClusterStatusSummary rows={group.rows} />
            </div>
            <div>
              {group.rows.map((row) => (
                <PipelineStageRow
                  key={row.stageNumber}
                  row={row}
                  onSubmitDate={handleSubmitDate}
                  onOpenInspection={(stage) => setInspectionStage(stage)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Inspection drawer */}
      {inspectionStage !== null ? (
        <InspectionEventDrawer
          open={inspectionStage !== null}
          onClose={() => setInspectionStage(null)}
          projectId={projectId}
          itemId={itemId}
          project={project}
          stage={inspectionStage}
          existingEvent={inspectionEventForStage}
          isReFunctionalCheck={isReFunctionalCheckCreate}
          attachmentCount={
            inspectionEventForStage
              ? countsQ.data?.byInspection[inspectionEventForStage.id] ?? 0
              : 0
          }
        />
      ) : null}
    </div>
  );
}

function ClusterStatusSummary({ rows }: { rows: StageRowState[] }) {
  const completed = rows.filter((r) => r.status === 'completed').length;
  const skipped = rows.filter((r) => r.status === 'skipped').length;
  const notApplicable = rows.filter((r) => r.status === 'not-applicable').length;
  const total = rows.length - notApplicable;
  if (total === 0) {
    return <span className="text-xs text-tertiary/60">N/A for sub-parts</span>;
  }
  return (
    <span className="text-xs text-secondary">
      {completed + skipped} / {total}
    </span>
  );
}
